import React, { useCallback, useState } from 'react';
import styled from 'styled-components/macro';
import { useIntl } from 'react-intl';
import Panel from '@tovia/man-ui/lib/components/Panel/Panel';
import { faDownload } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormErrors, SubmissionError } from 'redux-form';
import { PurchaseButton } from 'src/client/components/buttons/PurchaseButton';
import { PurchaseGalleryDialog } from '../PurchaseGallery/PurchaseGalleryDialog';
import { useSelector } from 'src/client/redux';
import { Gallery } from 'src/@types/Gallery';
import { ResponseError } from 'superagent';
import { useGetGalleryCoverUrl } from 'src/client/helpers/urls/useGetGalleryCoverUrl';
import { useGalleryPrices } from './useGalleryPrices';
import { usePurchaseGallery } from 'src/client/requests/usePurchaseGallery';
import { CurrencySymbols } from 'src/shared/constants/currency';

type GalleryPurchasePanelProps = {
  className?: string;
  purchased: boolean;
  gallery: Gallery;
};

export default function GalleryPurchasePanel(props: GalleryPurchasePanelProps) {
  const { className, purchased, gallery } = props;

  const {
    contentCdnUrl,
    config: { hasPPS },
  } = useSelector((state) => state.app);
  const user = useSelector((state) => state.auth.user);

  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const intl = useIntl();
  const getGalleryCover = useGetGalleryCoverUrl();
  const { products } = useGalleryPrices('gallery');
  const purchaseMutation = usePurchaseGallery();

  const [product] = products;

  const togglePurchase = useCallback(async () => {
    setShowPurchaseModal(!showPurchaseModal);
  }, [showPurchaseModal]);

  const purchaseScene = useCallback(
    async ({ email, password }) => {
      const data = {
        email,
        galleryUUID: gallery.UUID,
        password,
        quality: 'high',
      };

      try {
        await purchaseMutation.mutateAsync(data);
      } catch (err) {
        if (!email) {
          return;
        }

        const error = err as ResponseError;
        let message = 'Submission failed, please try again later';

        if (error?.response?.status === 409) {
          message = 'Please login to complete this purchase';
        }

        throw new SubmissionError({ email: message } as FormErrors<{
          email: string;
        }>);
      }
    },
    [gallery.UUID, purchaseMutation],
  );

  const showDownloads = !purchased && hasPPS && !props.gallery.downloadsDisabled;

  const price = parseFloat(product?.initialRegionalPriceWithoutVat || '0');
  const currency = CurrencySymbols[product?.currency || 'USD'];

  return (
    <PanelWrapper className={className}>
      <PurchasePanel>
        <div className="headline">
          {intl.formatMessage({
            id: 'galleryPurchasePanel.headline',
            defaultMessage: 'THE HIGHEST QUALITY OF EXCLUSIVE CONTENT',
          })}
        </div>
        <PurchaseButton
          topTitle={intl.formatMessage({
            id: 'gallery.buttons.purchaseButton.topTitle',
            defaultMessage: 'Access All Galleries Instantly',
          })}
          mainTitle={intl.formatMessage({
            id: 'gallery.buttons.purchaseButton.mainTitle',
            defaultMessage: 'Get Full Site Access',
          })}
        />
        {showDownloads && product && (
          <>
            <div>
              <small>
                {intl.formatMessage({ id: 'galleryPurchasePanel.alsoAvailable', defaultMessage: 'Also available' })}
              </small>
            </div>
            <a className="btn inline-block btn btn-download" onClick={togglePurchase}>
              <span className="icon">
                <FontAwesomeIcon icon={faDownload} />
              </span>
              <span className="no-dropdown">
                {intl.formatMessage(
                  { id: 'gallery.buttons.download', defaultMessage: `Download at ${currency}${price}` },
                  { price: `${currency}${price}` },
                )}
              </span>
            </a>
          </>
        )}
      </PurchasePanel>
      {product && (
        <PurchaseGalleryDialog
          currency={currency}
          description={gallery.description}
          onHide={togglePurchase}
          price={price}
          purchaseError={purchaseMutation.error}
          purchaseLoading={purchaseMutation.isLoading}
          purchaseRedirect={purchaseMutation.data?.redirectURL}
          purchaseScene={purchaseScene}
          purchaseStatus={purchaseMutation.isSuccess}
          quality="High"
          show={showPurchaseModal}
          submitted={!purchaseMutation.isIdle}
          thumbnail={getGalleryCover(contentCdnUrl, gallery, { thumbnail: true })}
          title={gallery.name}
          type="gallery"
          user={!!(user && user.email)}
          wideThumbnail={getGalleryCover(contentCdnUrl, gallery, { thumbnail: true })}
          year={new Date(gallery.publishedAt).getUTCFullYear()}
        />
      )}
    </PanelWrapper>
  );
}

const PanelWrapper = styled.div`
  text-align: center;

  & .headline {
    font-size: 0.875rem;

    @media (min-width: 992px) and (max-width: 1199px) {
      font-size: 0.625rem;
    }
  }

  & .btn {
    font-weight: bold;
    position: relative;
    text-align: center;
    margin: 5px auto;
    padding: 10px;
    font-size: 0.875rem;
    transition: opacity 0.3s;

    :hover {
      opacity: 0.9;
    }
  }

  & .icon {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    text-shadow: none;
  }

  & .btn.btn-download {
    width: 50%;
    min-width: 220px;
    padding-right: 12px;
    border-width: 1px;
    border-style: solid;
    background: #f9d083;
    color: #000;
    border-color: #be8c31;
    max-width: 250px;

    .icon {
      padding: 0 12px;
      line-height: 40px !important;
      background: rgba(255, 255, 255, 0.3);
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      text-shadow: none;
    }

    .text {
      padding-left: 35px;

      + span {
        float: right;
        margin-top: 7px;
      }
    }
  }

  & .btn .caret {
    margin-left: 8px;
  }

  .footer {
    color: ${(props) => props.theme.primary7};
  }

  & .purchase-modal {
    @media (max-width: 767px) {
      height: 100%;
      width: 100%;
    }
  }

  & .purchase-modal-body {
    padding: 20px 5px;
  }

  & .no-dropdown {
    margin-left: 33px;
  }

  & .dropdown-items {
    position: relative;
  }

  @media (min-width: 768px) and (max-width: 991px) {
    margin-top: 7.5px;
  }
`;

const PurchasePanel = styled(Panel)`
  margin-bottom: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;
